// Dyia in the top bar (doc 13 §2): a button that drops a chat down. Its answers are things the
// app does or shows — a page opened, a chart, a table of figures — with text around them.
import { ActionIcon, Badge, Button, Group, Popover, Table, Text, Textarea, Tooltip } from '@mantine/core';
import { ASSISTANT_NAME, type AssistantBlock, type QuerySpec } from '@twinlink/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiCheck, FiCompass, FiLink, FiPlusSquare, FiSend, FiTrash2, FiX } from 'react-icons/fi';
import { LuSparkles } from 'react-icons/lu';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { chartPalette } from '@web/app/theme';
import { TimeSeriesChart, type ChartSeries } from '@web/components/charts/TimeSeriesChart';
import { useSeriesQuery, useSignals } from '@web/features/data/api';
import { AddToDashboard } from '@web/features/explorer/AddToDashboard';
import { errorMessage, notifySuccess } from '@web/lib/errors';
import { formatDateTime, formatNumber } from '@web/lib/format';
import { useOrgContext, usePermissions } from '@web/lib/org';
import { orgUnits } from '@web/lib/units';
import { useAskDyia, useRunAction } from './api';
import classes from './Dyia.module.css';
import { applyEvent, explorerPath, fill, historyOf, targetPath, WAITING, type ActionBlock, type ActionState, type Turn } from './model';
import { useDyia } from './store';

type ChartBlockData = Extract<AssistantBlock, { type: 'chart' }>;
type StatsBlockData = Extract<AssistantBlock, { type: 'stats' }>;

function RemoveButton({ onRemove }: { onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <Tooltip label={t('dyia.remove')}>
      <ActionIcon size="sm" variant="subtle" color="gray" aria-label={t('dyia.remove')} onClick={onRemove}>
        <FiX size={14} />
      </ActionIcon>
    </Tooltip>
  );
}

/** Drawn from a query the browser sends itself: the chart shows what the user may read. */
function ChartBlock({ orgId, block, onRemove }: { orgId: string; block: ChartBlockData; onRemove: () => void }) {
  const { t, i18n } = useTranslation();
  const permissions = usePermissions();
  const org = useOrgContext(orgId);
  const [adding, setAdding] = useState(false);
  const spec = useMemo<QuerySpec>(() => ({ range: block.range, maxPoints: 300, fill: 'null', series: block.series.map((s) => ({ refId: s.signalId, signalId: s.signalId })) }), [block]);
  const query = useSeriesQuery(orgId, spec);
  const signals = useSignals(orgId, {}, adding);
  const ids = new Set(block.series.map((s) => s.signalId));
  const results = new Map((query.data?.series ?? []).map((s) => [s.refId, s]));
  const series: ChartSeries[] = block.series.map((s, i) => {
    const r = results.get(s.signalId);
    return { id: s.signalId, name: s.name, unit: orgUnits.unit(r?.displayUnitId ?? '')?.symbol ?? '', points: r?.access === 'ok' ? r.points : [], color: chartPalette[i % chartPalette.length] };
  });
  const link = explorerPath(orgId, block);
  return (
    <div className={classes.card}>
      <div className={classes.cardHead}>
        <Text size="sm" fw={600} c="var(--tl-text-strong)" truncate>
          {block.title}
        </Text>
        <RemoveButton onRemove={onRemove} />
      </div>
      {query.isError ? (
        <Text size="xs" c="red" role="alert">
          {errorMessage(query.error)}
        </Text>
      ) : (
        <TimeSeriesChart
          series={series}
          height={190}
          from={query.data ? Date.parse(query.data.from) : undefined}
          to={query.data ? Date.parse(query.data.to) : undefined}
          timeZone={org.data?.settings.timeZone}
          locale={i18n.resolvedLanguage}
          loading={query.isPending}
          ariaLabel={block.title}
        />
      )}
      <Group gap={6} mt={8}>
        {permissions.has('explorer.use') && (
          <Button component={Link} to={link} size="compact-xs" variant="light" leftSection={<FiCompass size={12} />}>
            {t('dyia.openExplorer')}
          </Button>
        )}
        {permissions.has('explorer.use') && (
          <Button
            size="compact-xs"
            variant="default"
            leftSection={<FiLink size={12} />}
            onClick={() => void navigator.clipboard.writeText(new URL(link, window.location.origin).toString()).then(() => notifySuccess(t('dyia.linkCopied')))}
          >
            {t('dyia.copyLink')}
          </Button>
        )}
        {permissions.has('dashboards.edit') && (
          <Button size="compact-xs" variant="default" leftSection={<FiPlusSquare size={12} />} onClick={() => setAdding(true)}>
            {t('dyia.addToDashboard')}
          </Button>
        )}
      </Group>
      <AddToDashboard orgId={orgId} opened={adding && !!signals.data} signals={(signals.data ?? []).filter((s) => ids.has(s.id))} units={{}} onClose={() => setAdding(false)} />
    </div>
  );
}

function StatsBlock({ block, timeZone, onRemove }: { block: StatsBlockData; timeZone?: string; onRemove: () => void }) {
  const { t } = useTranslation();
  const cell = (v: number | null) => (v === null ? '—' : formatNumber(v, Math.abs(v) >= 100 ? 0 : 2));
  return (
    <div className={classes.card}>
      <div className={classes.cardHead}>
        <div style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} c="var(--tl-text-strong)" truncate>
            {block.title}
          </Text>
          <Text size="xs" c="dimmed">
            {formatDateTime(block.range.from, timeZone)} → {formatDateTime(block.range.to, timeZone)}
          </Text>
        </div>
        <RemoveButton onRemove={onRemove} />
      </div>
      <Table.ScrollContainer minWidth={340}>
        <Table verticalSpacing={4} horizontalSpacing={6} fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('dyia.stats.signal')}</Table.Th>
              <Table.Th ta="right">{t('dyia.stats.min')}</Table.Th>
              <Table.Th ta="right">{t('dyia.stats.max')}</Table.Th>
              <Table.Th ta="right">{t('dyia.stats.mean')}</Table.Th>
              <Table.Th ta="right">{t('dyia.stats.last')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {block.rows.map((r) => (
              <Table.Tr key={r.signalId}>
                <Table.Td>
                  {r.name}
                  {r.unit ? <span className="tl-caption"> ({r.unit})</span> : null}
                </Table.Td>
                <Table.Td ta="right" className="tl-num">
                  {cell(r.min)}
                </Table.Td>
                <Table.Td ta="right" className="tl-num">
                  {cell(r.max)}
                </Table.Td>
                <Table.Td ta="right" className="tl-num">
                  {cell(r.mean)}
                </Table.Td>
                <Table.Td ta="right" className="tl-num">
                  {cell(r.last)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  );
}

/**
 * Changes Dyia prepared (doc 13 §3). Nothing happens until Confirm; then the steps are sent one
 * after the other, as the user, and the card says how far they went.
 */
function ActionCard({ orgId, block, state, onState }: { orgId: string; block: ActionBlock; state: ActionState; onState: (state: ActionState) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const run = useRunAction(orgId);
  const confirm = () => {
    let done = 0;
    onState({ status: 'running', done });
    run.mutate(
      {
        action: block,
        onStep: (n) => {
          done = n;
          onState({ status: 'running', done });
        },
      },
      {
        onSuccess: (ids) => {
          onState({ status: 'done', done: block.steps.length });
          if (block.open) navigate(targetPath(orgId, fill(block.open, ids)));
        },
        onError: (e) => onState({ status: 'failed', done, error: errorMessage(e) }),
      },
    );
  };
  const mark = (i: number) => (i < state.done ? '✓' : state.status === 'failed' && i === state.done ? '✕' : `${i + 1}.`);
  return (
    <div className={classes.card} data-danger={block.danger || undefined}>
      <div className={classes.cardHead}>
        <Text size="sm" fw={600} c="var(--tl-text-strong)">
          {t('dyia.action.title', { count: block.steps.length })}
        </Text>
        {state.status !== 'waiting' && state.status !== 'running' && (
          <Badge size="sm" variant="light" color={state.status === 'done' ? 'green' : state.status === 'failed' ? 'red' : 'gray'} leftSection={state.status === 'done' ? <FiCheck size={10} /> : undefined}>
            {t(`dyia.action.${state.status}`)}
          </Badge>
        )}
      </div>
      <ol className={classes.steps}>
        {block.steps.map((s, i) => (
          <li key={i} data-done={i < state.done || undefined}>
            <span className={classes.stepMark} aria-hidden>
              {mark(i)}
            </span>
            <span>{t(`dyia.steps.${s.kind}`, { ...s.params, defaultValue: s.label })}</span>
          </li>
        ))}
      </ol>
      {state.status === 'failed' && (
        <Text size="xs" c="red" role="alert" mt={6}>
          {t('dyia.action.failedAt', { done: state.done, total: block.steps.length })} {state.error}
        </Text>
      )}
      {(state.status === 'waiting' || state.status === 'running') && (
        <Group gap={8} mt={10} justify="flex-end">
          <Button size="compact-sm" variant="default" disabled={state.status === 'running'} onClick={() => onState({ status: 'cancelled', done: 0 })}>
            {t('common.cancel')}
          </Button>
          <Button size="compact-sm" color={block.danger ? 'red' : undefined} loading={state.status === 'running'} onClick={confirm}>
            {t('dyia.action.confirm')}
          </Button>
        </Group>
      )}
    </div>
  );
}

function Answer({ orgId, turn, timeZone }: { orgId: string; turn: Turn; timeZone?: string }) {
  const { t } = useTranslation();
  const removeBlock = useDyia((s) => s.removeBlock);
  const setAction = useDyia((s) => s.setAction);
  if (turn.error && turn.blocks.length === 0) {
    return (
      <Text size="sm" c="red" role="alert">
        {turn.error}
      </Text>
    );
  }
  if (turn.blocks.length === 0) {
    if (turn.pending) return null;
    return (
      <Text size="sm" c="dimmed">
        {t('dyia.noAnswer')}
      </Text>
    );
  }
  return (
    <div className={classes.answer}>
      {turn.blocks.map((b, i) => {
        if (b.type === 'text') {
          return (
            <div key={i} className={classes.text}>
              {b.text}
            </div>
          );
        }
        if (b.type === 'navigate') {
          return (
            <Link key={i} to={targetPath(orgId, b.path)} className={classes.action}>
              <FiArrowRight size={12} aria-hidden />
              {t('dyia.opened', { page: b.label })}
            </Link>
          );
        }
        if (b.type === 'chart') return <ChartBlock key={i} orgId={orgId} block={b} onRemove={() => removeBlock(turn.id, b)} />;
        if (b.type === 'action') return <ActionCard key={i} orgId={orgId} block={b} state={turn.actions?.[i] ?? WAITING} onState={(state) => setAction(turn.id, i, state)} />;
        return <StatsBlock key={i} block={b} timeZone={timeZone} onRemove={() => removeBlock(turn.id, b)} />;
      })}
      {turn.error && (
        <Text size="sm" c="red" role="alert">
          {turn.error}
        </Text>
      )}
    </div>
  );
}

export function Dyia({ orgId }: { orgId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const org = useOrgContext(orgId);
  const { opened, turns, setOpened, enter, add, update, clear } = useDyia();
  const ask = useAskDyia(orgId);
  const [draft, setDraft] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  useEffect(() => enter(orgId), [enter, orgId]);
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [turns, ask.isPending, opened]);
  useEffect(() => {
    if (opened) input.current?.focus();
  }, [opened]);

  const send = (text: string) => {
    const question = text.trim();
    if (!question || ask.isPending) return;
    setDraft('');
    add({ role: 'user', text: question, blocks: [] });
    const history = historyOf([...turns, { id: 'new', role: 'user', text: question, blocks: [] }]);
    // The answer is a turn from the start: it fills up as the text and the blocks arrive.
    const answer = add({ role: 'assistant', text: '', blocks: [], pending: true });
    ask.mutate(
      {
        body: { messages: history, page: location.pathname, language: i18n.resolvedLanguage },
        onEvent: (event) => {
          update(answer, (turn) => ({ blocks: applyEvent(turn.blocks, event) }));
          // The page opens as soon as Dyia decides it, while it goes on writing.
          if (event.t === 'block' && event.block.type === 'navigate') navigate(targetPath(orgId, event.block.path));
        },
      },
      {
        onSuccess: () => update(answer, () => ({ pending: false })),
        onError: (e) => update(answer, () => ({ pending: false, error: errorMessage(e) })),
      },
    );
  };

  const suggestions = [t('dyia.suggest.alerts'), t('dyia.suggest.summary'), t('dyia.suggest.dashboards')];
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width="min(460px, calc(100vw - 16px))" shadow="lg" radius="md" closeOnClickOutside={false} trapFocus={false} keepMounted>
      <Popover.Target>
        <Button variant={opened ? 'filled' : 'light'} size="compact-md" leftSection={<LuSparkles size={15} />} aria-expanded={opened} aria-haspopup="dialog" styles={{ label: { textTransform: 'none' } }} onClick={() => setOpened(!opened)}>
          {ASSISTANT_NAME}
        </Button>
      </Popover.Target>
      <Popover.Dropdown className={classes.panel}>
        <div className={classes.head}>
          <span className={classes.mark} aria-hidden>
            <LuSparkles size={15} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={700} c="var(--tl-text-strong)">
              {ASSISTANT_NAME}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {t('dyia.subtitle')}
            </Text>
          </div>
          {turns.length > 0 && (
            <Tooltip label={t('dyia.clear')}>
              <ActionIcon variant="subtle" color="gray" aria-label={t('dyia.clear')} onClick={clear} disabled={ask.isPending}>
                <FiTrash2 size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          <ActionIcon variant="subtle" color="gray" aria-label={t('common.close')} onClick={() => setOpened(false)}>
            <FiX size={18} />
          </ActionIcon>
        </div>

        <div className={classes.turns} aria-live="polite">
          {turns.length === 0 && (
            <div className={classes.empty}>
              <Text size="sm" c="var(--tl-text)">
                {t('dyia.hello')}
              </Text>
              {suggestions.map((s) => (
                <Button key={s} size="compact-sm" variant="default" styles={{ label: { textTransform: 'none' } }} onClick={() => send(s)}>
                  {s}
                </Button>
              ))}
            </div>
          )}
          {turns.map((turn) =>
            turn.role === 'user' ? (
              <div key={turn.id} className={classes.user}>
                {turn.text}
              </div>
            ) : (
              <Answer key={turn.id} orgId={orgId} turn={turn} timeZone={org.data?.settings.timeZone} />
            ),
          )}
          {ask.isPending && (
            <span className={classes.dots} role="status" aria-label={t('dyia.working')}>
              <span />
              <span />
              <span />
            </span>
          )}
          <div ref={end} />
        </div>

        <form
          className={classes.ask}
          onSubmit={(e) => {
            e.preventDefault();
            send(draft);
          }}
        >
          <Textarea
            ref={input}
            style={{ flex: 1 }}
            autosize
            minRows={1}
            maxRows={4}
            maxLength={4000}
            placeholder={t('dyia.placeholder')}
            aria-label={t('dyia.placeholder')}
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                send(draft);
              }
            }}
          />
          <ActionIcon type="submit" size="lg" variant="filled" aria-label={t('dyia.send')} disabled={!draft.trim()} loading={ask.isPending}>
            <FiSend size={16} />
          </ActionIcon>
        </form>
      </Popover.Dropdown>
    </Popover>
  );
}
